/**
 * 用户管理的数据请求（TanStack Query），接口与参数与旧版页面一致
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usersApi } from '@/api/services/users'
import { adminApi } from '@/api/services/admin'
import type { AdminUserDetail, AdminUserListItem, UserListQuery } from './types'

const USERS_KEY = ['admin', 'users'] as const
const VOCABULARY_KEY = ['admin', 'user-vocabulary'] as const

export function useUserList(query: UserListQuery) {
  return useQuery({
    queryKey: [...USERS_KEY, 'list', query],
    queryFn: async () => {
      const response = await usersApi.getList({
        page: query.page,
        page_size: query.pageSize,
        search: query.search || undefined,
        status: query.status
      })
      const payload: any = response.data ?? {}
      const users: AdminUserListItem[] = Array.isArray(payload.data) ? payload.data : []
      return { users, total: (payload.pagination?.total ?? payload.total ?? users.length) as number }
    },
    placeholderData: keepPreviousData
  })
}

// 详情抽屉打开时才请求（后端直接返回用户对象）
export function useUserDetail(userId: number | null) {
  return useQuery({
    queryKey: [...USERS_KEY, 'detail', userId],
    enabled: userId !== null,
    queryFn: async () => (await usersApi.getDetail(userId as number)).data as unknown as AdminUserDetail
  })
}

export function useUpdateUserStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userId, isActive }: { userId: number; isActive: boolean }) =>
      usersApi.updateStatus(userId, isActive),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY })
  })
}

export function useBatchUpdateUserStatus() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ userIds, isActive }: { userIds: number[]; isActive: boolean }) =>
      usersApi.batchUpdateStatus(userIds, isActive),
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY })
  })
}

export function useAnonymizeUser() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (userId: number) => usersApi.anonymize(userId),
    // 匿名化不可恢复，不自动重试
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: USERS_KEY })
  })
}

export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({ userId, password }: { userId: number; password: string }) =>
      usersApi.resetPassword(userId, password),
    retry: false
  })
}

// 用户词表：按熟悉度分值降序分页，只传分页参数
export function useUserVocabulary(userId: number, page: number, pageSize: number) {
  return useQuery({
    queryKey: [...VOCABULARY_KEY, userId, page, pageSize],
    queryFn: async () => {
      const response = await adminApi.getUserVocabulary(userId, { page, page_size: pageSize })
      return response.data.success && response.data.data ? response.data.data : null
    },
    // 翻页时保留上一页数据；换了用户不沿用旧用户的数据
    placeholderData: (previous, previousQuery) => (previousQuery?.queryKey[2] === userId ? previous : undefined)
  })
}

export function useWordEvidence(userId: number, lexemeId: number) {
  return useQuery({
    queryKey: [...VOCABULARY_KEY, 'evidence', userId, lexemeId],
    queryFn: async () => {
      const response = await adminApi.getWordEvidence(userId, lexemeId)
      if (response.data.success && response.data.data) return response.data.data
      throw new Error(response.data.message || '获取单词证据失败')
    }
  })
}

export function useClearUserData() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (userId: number) => (await adminApi.clearAllUserData(userId)).data,
    retry: false,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: VOCABULARY_KEY })
  })
}
